import {DragOnly} from "components/Accessibility.styles";
import {useMessages} from "i18n";
import {AddControl, ArrivalHint, Controls, Hero, Sub} from "./AddHero.styles";
import {useBrowse} from "./PageDropLayer";

export interface AddHeroProps {
	/**
	 * A hint that replaces the drop hint and shows on EVERY device: the limit-wall email's arrival
	 * (kitecorp/transferify-ui#3197), where the api kept the size the sender tried to send but never the
	 * files, so the empty area asks for them back. It states no size, for the reason the sub-line below
	 * states none. Absent on every ordinary visit.
	 */
	hint?: string;
}

/**
 * Empty-state drop hero: the ONLY thing the create card shows before any file is picked. It offers
 * two equal picker buttons — files and a folder (owner decision 2026-09-15, ui#1314) — inside the
 * quiet dashed surface approved in ui#2022.
 *
 * The surface itself stopped being the button when the second control arrived: nesting buttons is
 * invalid markup, and one button named after both pickers could not say which one a click would run.
 * Each control is now its own tab stop with its own name, and the folder half is the only way to send
 * a folder from a phone, where the drop hint below cannot apply (ui#1306).
 *
 * The sub-line reminds the user the whole viewport accepts drops, but only where a pointer can
 * actually drag (ui#1306, see {@link DragOnly}). Mode tabs and the recipient form appear only once
 * files exist (see TransferPanel). The view remains token-driven and localized through `sender.*`.
 *
 * <b>The drop area states no transfer size, in any state.</b> Owner decision, 2026-08-27: the sub-line
 * used to state the largest transfer the product carries while the consent gate was up (ui#1549), and
 * that claim went with the capability pitch that sat above this hero. The hero takes no props at all
 * any more because it has no claim left to vary — the composer is where a size still matters, and it
 * says so at the moment it does: the over-limit quote (ui#1454) or the upgrade nudge once a selection
 * actually exceeds the sender's plan.
 */
export function AddHero({hint}: AddHeroProps = {}) {
	const {browseFiles, browseFolder} = useBrowse();
	const {sender} = useMessages();
	return (
		<Hero data-testid="drop-hero">
			<Controls>
				<AddControl type="button" onClick={browseFiles}>{sender.addFiles}</AddControl>
				<AddControl type="button" onClick={browseFolder}>{sender.addFolder}</AddControl>
			</Controls>
			{/* The whole line is drag-only, so `DragOnly` has to be the OUTER element: as an inner
			    wrapper it leaves an empty `Sub` behind wherever no pointer can drag, and an empty flex
			    item still spends the hero's gap — a hole under the control on every phone (ui#1306).
			    The arrival hint is the one line that is NOT drag-only: it asks for files, not a drop. */}
			{hint === undefined
				? <DragOnly><Sub>{sender.dropHint}</Sub></DragOnly>
				: <ArrivalHint data-testid="drop-hero-arrival">{hint}</ArrivalHint>}
		</Hero>
	);
}
